import React, { useEffect, useRef, useState } from "react";
import { Alert, ArrowLeft, ArrowRight, Badge, Button, Checkbox, EmptyState, Hourglass03, Minus, Plus, TextInput, Textarea } from "@opendate-app/setlist";
import TurnstileChallenge from "../../../shared/TurnstileChallenge";
import TurnstileDisclosure from "../../../shared/TurnstileDisclosure";
import { formatPrice } from "../../../shared/teamCurrencyFormatting";
import { attendeeFieldErrors } from "../useClassRegistrationCheckout";
import { useClassRegistrationCheckoutContext } from "../ClassRegistrationCheckoutContext";
import OrderSummary from "./OrderSummary";

function QuantityPicker({ count, maxQuantity, unitPrice, discountHint, onAdd, onRemove }) {
  const stepperRef = useRef(null);
  const focusAddAfterRemove = useRef(false);

  useEffect(() => {
    if (!focusAddAfterRemove.current || count !== 1) return;
    focusAddAfterRemove.current = false;
    stepperRef.current.querySelector("[data-attendee-add]").focus();
  }, [count]);

  const remove = () => {
    focusAddAfterRemove.current = count === 2;
    onRemove();
  };

  return (
    <div className="pc-panel pc-qty" data-testid="attendee-quantity">
      <div>
        <p className="pc-qty__label" id="pc-qty-label">Number of attendees</p>
        <p className="pc-qty__hint">{[unitPrice && `${unitPrice} per person`, discountHint].filter(Boolean).join(" · ")}</p>
      </div>
      <div className="pc-qty__stepper" role="group" aria-labelledby="pc-qty-label" ref={stepperRef}>
        <Button variant="tertiary" icon={Minus} onClick={remove} disabled={count <= 1} aria-label="Remove an attendee" />
        <output aria-live="polite">{count}</output>
        <Button variant="tertiary" icon={Plus} onClick={onAdd} disabled={count >= maxQuantity} aria-label="Add an attendee" data-attendee-add="" />
      </div>
    </div>
  );
}

function PromoCodeField({ checkout }) {
  const [code, setCode] = useState("");
  const [announcement, setAnnouncement] = useState("");
  const panelRef = useRef(null);
  const applied = Boolean(checkout.appliedPromoCode);
  const wasApplied = useRef(applied);

  useEffect(() => {
    if (wasApplied.current === applied) return;
    wasApplied.current = applied;
    panelRef.current.querySelector(applied ? "button" : "input").focus();
  }, [applied]);

  const apply = async (event) => {
    event.preventDefault();
    if (checkout.applyingPromo) return;
    setAnnouncement("Checking promo code…");
    if (await checkout.applyPromoCode(code)) {
      setCode("");
      setAnnouncement("Promo code applied.");
    } else {
      setAnnouncement("");
    }
  };

  const remove = () => {
    checkout.removePromoCode();
    setAnnouncement("Promo code removed.");
  };

  return (
    <div ref={panelRef}>
      <span className="sr-only" role="status">{announcement}</span>
      <span className="sr-only" role="alert">{checkout.promoError || ""}</span>
      {applied ? (
        <div className="pc-panel pc-promo" data-testid="promo-code">
          <p className="pc-promo__applied mb-0">
            <Badge color="success">{checkout.appliedPromoCode}</Badge>
            <span>{checkout.quote?.discount_source === "group_discount" ? "Your group discount saves at least as much, so this code isn't used" : "Promo code applied"}</span>
          </p>
          <Button variant="tertiary" size="sm" onClick={remove}>Remove</Button>
        </div>
      ) : (
        <form className="pc-panel pc-promo" data-testid="promo-code" onSubmit={apply}>
          <div className="pc-promo__input">
            <TextInput
              label="Have a promo code?"
              placeholder="Enter code"
              value={code}
              error={checkout.promoError || undefined}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
          </div>
          <Button type="submit" variant="secondary" disabled={!code.trim()} aria-disabled={checkout.applyingPromo || undefined}>
            {checkout.applyingPromo ? "Checking…" : "Apply"}
          </Button>
        </form>
      )}
    </div>
  );
}

function CapacityLost({ classUrl, waitlistOpen }) {
  const panelRef = useRef(null);
  useEffect(() => { panelRef.current.focus(); }, []);

  return (
    <div ref={panelRef} tabIndex={-1} className="pc-panel" role="alert" data-testid="capacity-lost">
      <EmptyState
        icon={Hourglass03}
        title={<span role="heading" aria-level={1}>Someone took the last spot</span>}
        description={waitlistOpen ? "This class filled up before we could hold your spot. Join the waitlist and we'll email you if a spot opens." : "This class filled up before we could hold your spot. You haven't been charged."}
        primaryAction={waitlistOpen ? { label: "Join Waitlist", as: "a", href: `${classUrl}#waitlist` } : { label: "Back to Class", as: "a", href: classUrl }}
        secondaryAction={waitlistOpen ? { label: "Back to Class", as: "a", href: classUrl } : undefined}
      />
    </div>
  );
}

export default function AttendeesStep() {
  const { checkout, classInstance, classCard, classUrl, currencyCode, holdExpiresAt, maxQuantity, marketingOptinText, quotesUrl, turnstileSiteKey } = useClassRegistrationCheckoutContext();
  const turnstileRef = useRef(null);
  const attendeesRef = useRef(null);
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [focusRequest, setFocusRequest] = useState(null);
  const { attendees } = checkout;
  const fieldErrors = attendees.map(attendeeFieldErrors);
  const fieldError = (index, field) => (showFieldErrors ? fieldErrors[index][field] : undefined);
  const instructorName = classInstance.instructorFirstName;
  const notePrompt = instructorName ? `Anything ${instructorName} should know? (optional)` : "Anything the instructor should know? (optional)";
  const discountHint = classInstance.groupDiscountOffered ? `Group discount of ${Number(classInstance.groupDiscountPercentage)}% for ${classInstance.groupDiscountThreshold}+` : null;
  const continueLabel = checkout.free ? "Register" : "Continue to Payment";
  const unitPrice = checkout.quote ? formatPrice(checkout.quote.unit_price, currencyCode) : null;
  const group = attendees.length > 1;
  const showInlineSummary = !classCard;

  const verify = async () => {
    if (!turnstileSiteKey || !turnstileRef.current) return null;
    try {
      return await turnstileRef.current.executeAsync();
    } catch (error) {
      turnstileRef.current.reset();
      throw error;
    }
  };

  useEffect(() => {
    if (!focusRequest) return;
    attendeesRef.current.querySelector(focusRequest.selector)?.focus();
  }, [focusRequest]);

  const submit = () => {
    if (fieldErrors.some((errors) => Object.keys(errors).length > 0)) {
      setShowFieldErrors(true);
      setFocusRequest({ selector: '[aria-invalid="true"]' });
      return;
    }
    checkout.placeOrder(verify);
  };

  const addAttendeeRow = () => {
    checkout.addAttendee();
    if (attendees.length + 1 >= maxQuantity) setFocusRequest({ selector: `[data-testid="checkout-attendee"]:nth-of-type(${attendees.length + 1}) input` });
  };

  const removeAttendeeRow = (index) => {
    checkout.removeAttendee(index);
    setFocusRequest({ selector: `[data-testid="checkout-attendee"]:nth-of-type(${index}) input` });
  };

  if (checkout.capacityLost) {
    return <CapacityLost classUrl={classUrl} waitlistOpen={checkout.capacityLost.waitlistOpen} />;
  }

  return (
    <div>
      <p className="pc-checkout__lede">
        {group ? "You're registering " : "You're registering 1 person for "}
        {group && `${attendees.length} people for `}
        <strong>{classInstance.name}</strong>
        {group ? ". Each attendee gets their own confirmation email." : "."}
      </p>
      {checkout.errors.length > 0 && (
        <div className="mb-3">
          <Alert
            role="alert"
            color={checkout.priceChanged ? "warning" : "error"}
            description={checkout.priceChanged ? "The price for this class changed. The total is updated. Review it and continue." : checkout.errors.join(" ")}
          />
        </div>
      )}
      {maxQuantity > 1 && !holdExpiresAt && (
        <QuantityPicker
          count={attendees.length}
          maxQuantity={maxQuantity}
          unitPrice={unitPrice}
          discountHint={discountHint}
          onAdd={addAttendeeRow}
          onRemove={() => checkout.removeAttendee(attendees.length - 1)}
        />
      )}
      <div ref={attendeesRef}>
        {attendees.map((attendee, index) => (
          <fieldset key={index} className="pc-panel pc-attendee" data-testid="checkout-attendee">
            <legend className="sr-only">{`Attendee ${index + 1}`}</legend>
            <div className="pc-attendee__header">
              <span className="pc-attendee__number" aria-hidden="true">{index + 1}</span>
              <p className="pc-attendee__title">{index === 0 && group ? "Attendee 1 — that's you" : `Attendee ${index + 1}`}</p>
              {index === 0 && group && <Badge color="brand">Primary contact</Badge>}
              {index > 0 && (
                <Button variant="tertiary" size="sm" aria-label={`Remove attendee ${index + 1}`} onClick={() => removeAttendeeRow(index)}>Remove</Button>
              )}
            </div>
            <div className="pc-attendee__row">
              <TextInput label="First name" required autoComplete={index === 0 ? "given-name" : "off"} placeholder="First name" value={attendee.first_name} error={fieldError(index, "first_name")} onChange={(e) => checkout.updateAttendee(index, "first_name", e.target.value)} />
              <TextInput label="Last name" required autoComplete={index === 0 ? "family-name" : "off"} placeholder="Last name" value={attendee.last_name} error={fieldError(index, "last_name")} onChange={(e) => checkout.updateAttendee(index, "last_name", e.target.value)} />
            </div>
            <TextInput
              label="Email"
              required
              type="email"
              autoComplete={index === 0 ? "email" : "off"}
              placeholder={index === 0 ? "you@example.com" : "them@example.com"}
              hint={index === 0 ? "Your confirmation and class reminders go here." : "Their own confirmation and class reminders go here."}
              value={attendee.email}
              error={fieldError(index, "email")}
              onChange={(e) => checkout.updateAttendee(index, "email", e.target.value)}
            />
            <TextInput label="Phone (optional)" type="tel" autoComplete={index === 0 ? "tel" : "off"} value={attendee.phone} onChange={(e) => checkout.updateAttendee(index, "phone", e.target.value)} />
            <Textarea
              label={notePrompt}
              rows={3}
              maxLength={2000}
              placeholder={index === 0 ? "Experience level, accessibility needs, what you're hoping to learn…" : "Experience level, accessibility needs, what they're hoping to learn…"}
              value={attendee.note_to_instructor}
              onChange={(e) => checkout.updateAttendee(index, "note_to_instructor", e.target.value)}
            />
          </fieldset>
        ))}
      </div>
      {attendees.length < maxQuantity && (
        <Button variant="secondary" icon={Plus} onClick={addAttendeeRow}>Add Another Attendee</Button>
      )}
      {quotesUrl && <div className="mt-3"><PromoCodeField checkout={checkout} /></div>}
      {showInlineSummary && <div className="mt-3"><OrderSummary /></div>}
      {!checkout.quote && <p className="pc-muted mt-3 mb-0" id="pc-checkout-no-quote">Online registration isn't available for a group of this size. Try a different number of attendees.</p>}
      {marketingOptinText && (
        <div className="mt-3">
          <Checkbox size="sm" label={marketingOptinText} checked={checkout.subscribeToVenue} onChange={(e) => checkout.setSubscribeToVenue(e.target.checked)} />
        </div>
      )}
      {turnstileSiteKey && (
        <div>
          <TurnstileChallenge ref={turnstileRef} sitekey={turnstileSiteKey} />
          <TurnstileDisclosure />
        </div>
      )}
      <div className="pc-checkout-actions">
        {!holdExpiresAt && <Button as="a" variant="secondary" icon={ArrowLeft} href={classUrl}>Back to Class</Button>}
        <Button
          variant="primary"
          trailingIcon={ArrowRight}
          onClick={submit}
          disabled={!checkout.quote}
          aria-disabled={checkout.working || undefined}
          aria-describedby={checkout.quote ? undefined : "pc-checkout-no-quote"}
        >
          {checkout.working ? "Holding your spots…" : continueLabel}
        </Button>
      </div>
    </div>
  );
}
